/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { clsx, type ClassValue } from "clsx";
import { extendTailwindMerge } from "tailwind-merge";

/**
 * The project's own font-size scale, from the `--text-*` tokens in
 * tokens-typography.css. Tailwind v4 turns each of those into a font-size
 * utility (`text-body`, `text-meta`, ...), but tailwind-merge only knows the
 * stock scale (`text-xs` ... `text-9xl`) and files every other `text-*` under
 * text-COLOUR. Two classes then land in one conflict group and the last one
 * wins by deleting the other:
 *
 *   twMerge('text-(--text-inverse) text-body') -> 'text-body'
 *
 * The ink vanished, silently, at merge time. That is how the user-avatar
 * button ended up with a #1a1a1a initial on a #4a0e11 fill (1.13:1) while
 * its source clearly asks for `text-(--text-inverse)`. Only the *bare* form
 * is affected — `text-[length:var(--text-meta)]` and `text-(length:--text-
 * small)` carry the type hint tailwind-merge needs, which is why the app has
 * just two of these and not dozens. Declaring the scale here fixes them
 * without reordering classes, and stops the next one from happening.
 *
 * Deliberately excludes `--text-primary`, `--text-inverse` and friends from
 * tokens.css: those are colours sharing the same variable prefix, so listing
 * them would create the mirror-image bug.
 */
const FONT_SIZE_UTILITIES = [
  "app-heading", "app-section", "app-subheading", "app-title",
  "body", "body-lg", "body-sm", "display", "eyebrow",
  "h1", "h2", "h3", "h5", "h6",
  "meta", "micro", "page-title", "settings-title", "small", "stat", "tag",
  "workspace-title",
] as const;

/**
 * `truncate` is three declarations in one — `overflow: hidden`,
 * `text-overflow: ellipsis` and `white-space: nowrap` — but tailwind-merge
 * files it under `text-overflow` alongside `text-ellipsis` and `text-clip`,
 * and that group declares no conflicts. So a component that bakes in a
 * white-space utility and lets callers append classes emits both:
 *
 *   cn('whitespace-pre-wrap m-0', 'truncate') -> 'whitespace-pre-wrap ... truncate'
 *
 * Same specificity, and `whitespace-pre-wrap` wins on source order in the
 * generated sheet. The name wraps, the ellipsis never appears, and nothing
 * warns. That is why every `<Text className="... truncate">` in the app --
 * 110 of them -- renders untruncated, including the workspace cards, where
 * the wrapped name then dragged the avatar out of line with its neighbours.
 *
 * `truncate` gets its own group rather than hanging the conflict on
 * `text-overflow`: `text-ellipsis` on its own does not imply `nowrap`, so
 * grouping them would make it delete a `whitespace-pre-wrap` that was asked
 * for on purpose. Declared one-way, so a `whitespace-*` written *after*
 * `truncate` still wins — the caller's later word is still the last word.
 */
const twMerge = extendTailwindMerge<"truncate">({
  extend: {
    classGroups: {
      "font-size": [{ text: [...FONT_SIZE_UTILITIES] }],
      truncate: ["truncate"],
    },
    conflictingClassGroups: {
      truncate: ["whitespace", "text-overflow", "overflow"],
    },
  },
});

/**
 * Utility function to merge Tailwind CSS classes with clsx and tailwind-merge.
 * This ensures proper class merging and conflict resolution for Tailwind utilities.
 *
 * @param inputs - Class values (strings, objects, arrays, etc.)
 * @returns Merged class string
 *
 * @example
 * ```tsx
 * cn("px-4 py-2", "bg-red-500", { "text-white": isActive })
 * // Returns: "px-4 py-2 bg-red-500 text-white" (if isActive is true)
 * ```
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Common Tailwind class combinations for frequently used patterns
 */
export const tailwindUtils = {
  /**
   * Button base styles — uses --radius-md (6px)
   */
  buttonBase: "inline-flex items-center justify-center rounded-(--radius-md) font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  
  /**
   * Card base styles — uses --radius-lg (12px)
   */
  card: "rounded-(--radius-lg) border border-[var(--border-subtle)] bg-[var(--surface-primary)] shadow-sm",
  
  /**
   * Input base styles — uses --radius-md (6px)
   */
  input: "flex h-10 w-full rounded-(--radius-md) border border-[var(--border-medium)] bg-[var(--surface-primary)] px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-[var(--text-tertiary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--seed-base)] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
  
  /**
   * Container max width
   */
  container: "mx-auto max-w-[var(--container-max-width)] px-[var(--container-padding)]",
  
  /**
   * Text color utilities
   */
  textPrimary: "text-[var(--text-primary)]",
  textSecondary: "text-[var(--text-secondary)]",
  textTertiary: "text-[var(--text-tertiary)]",
  
  /**
   * Surface color utilities
   */
  surfacePrimary: "bg-[var(--surface-primary)]",
  surfaceSecondary: "bg-[var(--surface-secondary)]",
  surfaceHover: "bg-[var(--surface-hover)]",
  
  /**
   * Spacing utilities using design system spacing
   */
  spacing: {
    p1: "p-[var(--space-1)]",
    p2: "p-[var(--space-2)]",
    p3: "p-[var(--space-3)]",
    p4: "p-[var(--space-4)]",
    p6: "p-[var(--space-6)]",
    p8: "p-[var(--space-8)]",
    gap1: "gap-[var(--space-1)]",
    gap2: "gap-[var(--space-2)]",
    gap4: "gap-[var(--space-4)]",
    gap6: "gap-[var(--space-6)]",
  },

  /**
   * Scroll region — the single designated overflow container inside a fixed shell.
   * Combines flex column, min-h-0 (flex shrink boundary), and vertical auto scroll.
   */
  shellScroll: "flex flex-col min-h-0 overflow-y-auto",

  /**
   * Fluid grid — responsive auto-fit grid that reflows columns under zoom/resize
   * instead of overflowing. Uses a 240px minimum column width.
   */
  fluidGrid: "grid grid-cols-[repeat(auto-fit,minmax(240px,1fr))]",

  /**
   * Full-width contained — fills the parent width without viewport breakout.
   * Replaces the w-screen + calc(-50vw + 50%) anti-pattern that causes
   * horizontal scroll under zoom.
   */
  fullWidthContained: "w-full shrink-0",
} as const;
